/**
 * Time taken against time allowed, one row per sitting.
 *
 * The app records both halves — `attempts.started_at`/`submitted_at` give the
 * wall clock the student actually used, and the exam's duration plus that
 * attempt's own `extra_time_min` gives what they were entitled to — so the
 * useful question is not "how long did this take" but "how much of the clock
 * did it eat". A student finishing every paper on the buzzer is a different
 * problem from one finishing at half time and still scoring badly.
 *
 * Everything is drawn against one shared minute scale so the rows compare, the
 * allowed length is marked on every bar rather than assumed constant (extra
 * time is granted per attempt), and an overrun is drawn past that mark AND
 * labelled "over" — the colour is never the only thing saying so.
 */
import { formatDuration } from "../lib/format";
import type { AttemptFact } from "./AnalyticsData";

const WIDTH = 640;
const ROW_H = 22;
const PAD = { top: 4, right: 96, bottom: 30, left: 150 };
const LABEL_CHARS = 26;

/** SVG has no ellipsis; the full string still reaches the reader via `title`. */
function clip(text: string): string {
  return text.length > LABEL_CHARS ? `${text.slice(0, LABEL_CHARS - 1)}…` : text;
}

/** Round the axis up to something a person would choose. */
function niceMax(minutes: number): number {
  const step = minutes <= 60 ? 15 : minutes <= 180 ? 30 : 60;
  return Math.max(step, Math.ceil(minutes / step) * step);
}

export function ChartTimeUse({ facts }: { facts: AttemptFact[] }) {
  // A row needs both halves to say anything; one without the other would be a
  // bar with no meaning rather than a bar with a hole in it.
  const rows = facts.filter(
    (f) => f.taken_ms !== null && f.allowed_ms !== null && f.allowed_ms > 0,
  );

  if (rows.length === 0) {
    return (
      <p className="px-4 py-6 text-xs text-ink-faint">
        No sitting yet has both a start and a submit time, so there is nothing to
        measure against the clock. This fills in as attempts are submitted.
      </p>
    );
  }

  const maxMinutes = niceMax(
    rows.reduce(
      (max, r) => Math.max(max, (r.taken_ms ?? 0) / 60_000, (r.allowed_ms ?? 0) / 60_000),
      1,
    ),
  );

  const plotW = WIDTH - PAD.left - PAD.right;
  const height = PAD.top + rows.length * ROW_H + PAD.bottom;
  const x = (minutes: number) => PAD.left + (minutes / maxMinutes) * plotW;
  const tickStep = maxMinutes <= 60 ? 15 : maxMinutes <= 180 ? 30 : 60;
  const ticks: number[] = [];
  for (let t = 0; t <= maxMinutes; t += tickStep) ticks.push(t);

  return (
    <div className="px-4 pb-4">
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="w-full"
        role="img"
        aria-label={`Time used against time allowed, in minutes, for ${rows.length} ${
          rows.length === 1 ? "sitting" : "sittings"
        }. ${rows
          .map(
            (r) =>
              `${r.exam_title}, sitting ${r.attempt_no}: took ${formatDuration(
                r.taken_ms ?? 0,
              )} of ${formatDuration(r.allowed_ms ?? 0)} allowed.`,
          )
          .join(" ")}`}
      >
        {ticks.map((tick) => (
          <line
            key={`grid-${tick}`}
            x1={x(tick)}
            x2={x(tick)}
            y1={PAD.top}
            y2={PAD.top + rows.length * ROW_H}
            stroke="var(--color-border)"
            strokeWidth={1}
          />
        ))}

        {rows.map((row, i) => {
          const takenMin = (row.taken_ms ?? 0) / 60_000;
          const allowedMin = (row.allowed_ms ?? 0) / 60_000;
          const over = takenMin > allowedMin;
          const y = PAD.top + i * ROW_H;
          const barY = y + 5;
          const barH = ROW_H - 12;
          const usedRatio = allowedMin > 0 ? takenMin / allowedMin : 0;

          return (
            <g key={row.attempt_id}>
              <title>
                {`${row.exam_title} (sitting ${row.attempt_no}) — took ${formatDuration(
                  row.taken_ms ?? 0,
                )} of ${formatDuration(row.allowed_ms ?? 0)} allowed, ${Math.round(
                  usedRatio * 100,
                )}%${over ? ", over the allowance" : ""}`}
              </title>

              <text
                x={PAD.left - 8}
                y={y + ROW_H / 2 + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--color-ink-muted)"
              >
                {clip(row.exam_title)}
                {row.attempt_no > 1 ? ` #${row.attempt_no}` : ""}
              </text>

              {/* The allowance, as an empty track. */}
              <rect
                x={x(0)}
                y={barY}
                width={Math.max(1, x(allowedMin) - x(0))}
                height={barH}
                fill="var(--color-surface-sunken)"
                rx={2}
              />
              {/* What was actually used, clipped at the allowance… */}
              <rect
                x={x(0)}
                y={barY}
                width={Math.max(1, x(Math.min(takenMin, allowedMin)) - x(0))}
                height={barH}
                fill="var(--color-ink-muted)"
                rx={2}
              />
              {/* …and the overrun beyond it, which is a different fact. */}
              {over ? (
                <rect
                  x={x(allowedMin)}
                  y={barY}
                  width={Math.max(1, x(takenMin) - x(allowedMin))}
                  height={barH}
                  fill="var(--color-violation)"
                  rx={2}
                />
              ) : null}
              {/* The allowance mark itself — extra time moves it per attempt. */}
              <line
                x1={x(allowedMin)}
                x2={x(allowedMin)}
                y1={barY - 3}
                y2={barY + barH + 3}
                stroke="var(--color-border-strong)"
                strokeWidth={1.5}
              />

              <text
                x={WIDTH - PAD.right + 8}
                y={y + ROW_H / 2 + 3}
                fontSize={9}
                fill="var(--color-ink-muted)"
              >
                {formatDuration(row.taken_ms ?? 0)} / {formatDuration(row.allowed_ms ?? 0)}
              </text>
              {over ? (
                <text
                  x={WIDTH - 4}
                  y={y + ROW_H / 2 + 3}
                  textAnchor="end"
                  fontSize={9}
                  fontWeight={500}
                  fill="var(--color-violation)"
                >
                  over
                </text>
              ) : null}
            </g>
          );
        })}

        {/* Axis, in minutes, stated as such. */}
        <line
          x1={PAD.left}
          x2={WIDTH - PAD.right}
          y1={PAD.top + rows.length * ROW_H}
          y2={PAD.top + rows.length * ROW_H}
          stroke="var(--color-border-strong)"
          strokeWidth={1}
        />
        {ticks.map((tick) => (
          <text
            key={`tick-${tick}`}
            x={x(tick)}
            y={PAD.top + rows.length * ROW_H + 12}
            textAnchor="middle"
            fontSize={9}
            fill="var(--color-ink-faint)"
          >
            {tick}
          </text>
        ))}
        <text
          x={PAD.left}
          y={PAD.top + rows.length * ROW_H + 24}
          fontSize={9}
          fill="var(--color-ink-faint)"
        >
          minutes elapsed · the tick on each bar is that attempt&rsquo;s own allowance
        </text>
      </svg>
    </div>
  );
}
